import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Entete } from '../components/Entete'
import { useSession } from '../auth/session'
import { definirActif, definirRole, listerProfils, type Profil, type Role } from '../lib/profils'

/**
 * Administration des acces.
 *
 * PERIMETRE V1 VOLONTAIREMENT REDUIT : cette page active, desactive et change le
 * role d'un compte EXISTANT. Elle ne cree pas de compte.
 *
 * Creer un compte depuis le front suppose l'API d'administration, donc la cle
 * secrete, donc une Edge Function dediee avec sa propre authentification et ses
 * propres tests — un lot en soi. En V1, un admin cree le compte depuis le
 * tableau de bord Supabase, puis l'active ici. La procedure est au README.
 *
 * Le compte naît inactif (trigger de la migration 0006) : il n'y a donc pas de
 * fenetre pendant laquelle un compte fraichement cree verrait les dossiers.
 */
export default function Administration() {
  const etat = useSession()
  const fileDAttente = useQueryClient()
  const [aConfirmer, setAConfirmer] = useState<Profil | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const profils = useQuery({ queryKey: ['profils'], queryFn: listerProfils })

  const rafraichir = () => {
    setErreur(null)
    void fileDAttente.invalidateQueries({ queryKey: ['profils'] })
  }

  const mutationActif = useMutation({
    mutationFn: ({ id, actif }: { id: string; actif: boolean }) => definirActif(id, actif),
    onSuccess: rafraichir,
    onError: (e: Error) => setErreur(e.message),
  })

  const mutationRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => definirRole(id, role),
    onSuccess: rafraichir,
    onError: (e: Error) => setErreur(e.message),
  })

  if (etat.statut !== 'connecte') return null
  const moi = etat.profil

  return (
    <div className="min-h-screen bg-slate-50">
      <Entete profil={moi} />

      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-xl font-semibold text-slate-900">Administration des accès</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Cette page active, désactive et change le rôle d’un compte existant. La création d’un
          compte se fait depuis le tableau de bord Supabase&nbsp;; le compte apparaît ici, inactif,
          et vous l’activez.
        </p>

        {erreur ? (
          <p role="alert" data-test="erreur-administration" className="mt-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        ) : null}

        {aConfirmer ? (
          <div
            role="alertdialog"
            aria-labelledby="titre-confirmation"
            data-test="confirmation-desactivation"
            className="mt-6 rounded border border-amber-300 bg-amber-50 p-4"
          >
            <p id="titre-confirmation" className="text-sm text-amber-900">
              Désactiver le compte <strong>{aConfirmer.email}</strong>&nbsp;? Il perdra
              immédiatement l’accès à tous les dossiers.
            </p>
            <div className="mt-3 flex gap-3">
              <button
                type="button"
                data-test="confirmer-desactivation"
                onClick={() => {
                  mutationActif.mutate({ id: aConfirmer.id, actif: false })
                  setAConfirmer(null)
                }}
                className="rounded bg-marque-primaire-texte px-3 py-1 text-sm font-medium text-white hover:bg-marque-primaire-survol"
              >
                Désactiver {aConfirmer.email}
              </button>
              <button
                type="button"
                data-test="annuler-desactivation"
                onClick={() => setAConfirmer(null)}
                className="rounded border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-6">
          {profils.isPending ? (
            <p role="status" className="text-slate-600">Chargement des comptes…</p>
          ) : profils.isError ? (
            <p role="alert" className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800">
              La requête a échoué. Les comptes n’ont pas pu être chargés.
            </p>
          ) : (
            <table data-test="tableau-profils" className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-300 text-slate-600">
                  <th scope="col" className="py-2 pr-4 font-medium">Courriel</th>
                  <th scope="col" className="py-2 pr-4 font-medium">Rôle</th>
                  <th scope="col" className="py-2 pr-4 font-medium">État</th>
                  <th scope="col" className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {profils.data.map((profil) => {
                  const cestMoi = profil.id === moi.id
                  return (
                    <tr key={profil.id} data-test="ligne-profil" className="border-b border-slate-200">
                      <td className="py-2 pr-4 text-slate-900">
                        {profil.email}
                        {cestMoi ? <span className="ml-2 text-xs text-slate-500">(vous)</span> : null}
                      </td>
                      <td className="py-2 pr-4">
                        <select
                          aria-label={`Rôle de ${profil.email}`}
                          value={profil.role}
                          // Un admin ne retire pas son propre role : la base le
                          // refuse (trigger de 0006), l'ecran ne le propose pas.
                          disabled={cestMoi}
                          onChange={(e) =>
                            mutationRole.mutate({ id: profil.id, role: e.target.value as Role })
                          }
                          className="rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100 disabled:text-slate-500"
                        >
                          <option value="utilisateur">utilisateur</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td className="py-2 pr-4">
                        {profil.actif ? (
                          <span className="font-medium text-slate-900">actif</span>
                        ) : (
                          <span className="text-slate-500">désactivé</span>
                        )}
                      </td>
                      <td className="py-2">
                        {profil.actif ? (
                          <button
                            type="button"
                            data-test="bouton-desactiver"
                            disabled={cestMoi}
                            onClick={() => setAConfirmer(profil)}
                            className="rounded border border-slate-300 px-3 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                            title={cestMoi ? 'Vous ne pouvez pas désactiver votre propre compte' : undefined}
                          >
                            Désactiver
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-test="bouton-activer"
                            onClick={() => mutationActif.mutate({ id: profil.id, actif: true })}
                            className="rounded bg-marque-primaire-texte px-3 py-1 font-medium text-white hover:bg-marque-primaire-survol"
                          >
                            Activer
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  )
}
